import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { client } from '../sanityClient';
import { PortableText } from '@portabletext/react';
import { FaUser, FaCalendar, FaClock, FaShare, FaTwitter, FaLinkedin, FaFacebook, FaInstagram, FaWhatsapp } from 'react-icons/fa';

const samplePosts = {
  'test-blog': {
    title: 'Cybersecurity Fundamentals',
    content: 'Understanding cybersecurity fundamentals is crucial for protecting digital assets in today\'s interconnected world. This comprehensive guide covers essential security principles, threat identification, and best practices for maintaining robust security posture. Learn about encryption, access controls, network security, and incident response strategies that form the foundation of effective cybersecurity programs.',
    category: 'Fundamentals',
    readTime: '8 min read',
    author: 'Security Expert',
    mainImage: { asset: { url: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=500&h=300&fit=crop' }, alt: 'Cybersecurity Fundamentals' }
  },
  'advanced-threat-detection-ai': {
    title: 'Advanced Threat Detection with AI',
    content: 'Artificial intelligence is transforming cybersecurity by enabling faster, more accurate threat detection. Machine learning algorithms can analyze vast amounts of network data in real-time, identifying patterns that indicate potential security breaches. This proactive approach helps organizations stay ahead of cybercriminals who are constantly evolving their attack methods.',
    category: 'AI Security',
    readTime: '5 min read',
    author: 'Dr. Sarah Chen',
    mainImage: { asset: { url: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=500&h=300&fit=crop' }, alt: 'AI Threat Detection' }
  },
  'zero-trust-security-architecture': {
    title: 'Zero Trust Security Architecture', 
    content: 'Zero Trust is a security model that assumes no user or device should be trusted by default, even if they are inside the network perimeter. This approach requires verification for every user and device trying to access resources, regardless of their location. Implementing Zero Trust involves continuous monitoring, strict access controls, and comprehensive identity verification.',
    category: 'Architecture',
    readTime: '8 min read',
    author: 'Marcus Rodriguez',
    mainImage: { asset: { url: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=500&h=300&fit=crop' }, alt: 'Zero Trust Architecture' }
  },
  'cloud-security-best-practices': {
    title: 'Cloud Security Best Practices',
    content: 'Securing cloud infrastructure requires a multi-layered approach. Key practices include implementing strong identity and access management, encrypting data both in transit and at rest, regularly monitoring for suspicious activities, and maintaining proper backup and disaster recovery procedures. Organizations must also ensure compliance with relevant regulations and industry standards.',
    category: 'Cloud Security',
    readTime: '6 min read',
    author: 'Jennifer Kim',
    mainImage: { asset: { url: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=500&h=300&fit=crop' }, alt: 'Cloud Security' }
  },
  'incident-response-automation': {
    title: 'Incident Response Automation',
    content: 'Streamline your security operations with automated incident response workflows and AI-powered threat analysis. Modern security operations centers require rapid response capabilities to address the increasing volume and sophistication of cyber threats.',
    category: 'Automation',
    readTime: '7 min read',
    author: 'Alex Thompson',
    mainImage: { asset: { url: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=500&h=300&fit=crop' }, alt: 'Incident Response Automation' }
  },
  'ransomware-protection-strategies': {
    title: 'Ransomware Protection Strategies',
    content: 'Comprehensive strategies to protect your organization from ransomware attacks and ensure business continuity. Learn about prevention, detection, and recovery techniques that can save your organization from devastating attacks.',
    category: 'Threat Protection',
    readTime: '9 min read',
    author: 'David Park',
    mainImage: { asset: { url: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=500&h=300&fit=crop' }, alt: 'Ransomware Protection' }
  },
  'security-compliance-framework': {
    title: 'Security Compliance Framework',
    content: 'Navigate complex regulatory requirements with our comprehensive security compliance framework and audit tools. Understanding compliance requirements is essential for modern organizations.',
    category: 'Compliance',
    readTime: '10 min read',
    author: 'Lisa Wang',
    mainImage: { asset: { url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&h=300&fit=crop' }, alt: 'Security Compliance' }
  },
  'what-is-ai-really': {
    title: 'What Is AI, Really? A Plain-English Guide to What Artificial Intelligence Actually Does (and Doesn\'t)',
    category: 'AI Security',
    readTime: '10 min read',
    author: 'Cygne Noir Team',
    tags: ['AI', 'Artificial Intelligence', 'Machine Learning', 'Technology', 'Ethics'],
    publishedAt: new Date().toISOString(),
    mainImage: { asset: { url: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=500&h=300&fit=crop' }, alt: 'Artificial Intelligence Overview' },
    body: [
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'AI is everywhere — in news headlines, in boardrooms, in product pitches, and yes, even in memes. It\'s often described as the technology that will transform how we work, learn, shop, travel, and think. But let\'s be honest: Most people still have one big question — what exactly is AI, and what can it really do?'
          }
        ]
      },
      {
        _type: 'block',
        style: 'h2',
        children: [
          {
            _type: 'span',
            text: 'So… What Is AI, Really?'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'At its core, Artificial Intelligence is when machines can perform tasks that normally require human intelligence — like understanding language, recognizing images, spotting patterns, or making predictions. It\'s not a robot with feelings or consciousness. It\'s math, data, and pattern recognition working behind the scenes.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Think of it like this:'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Netflix learns your taste and suggests what you\'ll binge next.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Google Maps predicts traffic using data from millions of phones.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'ChatGPT (hello!) recognizes language patterns and responds like a human.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Email spam filters learn what junk mail looks like and block it automatically.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'All of that is AI quietly doing its job. It doesn\'t "think" the way humans do — it analyzes patterns and makes educated predictions. No emotions. No intuition. Just computation.'
          }
        ]
      },
      {
        _type: 'image',
        asset: { url: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=400&fit=crop' },
        alt: 'AI Technology Illustration'
      },
      {
        _type: 'block',
        style: 'h2',
        children: [
          {
            _type: 'span',
            text: 'When Machines Get It Wrong'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Here\'s the part people rarely mention: AI makes mistakes — and sometimes those mistakes can be unfair. Since AI learns from data, it also learns our human biases. A few real examples:'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Facial recognition systems have misidentified darker-skinned faces more often because the training data underrepresented them.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Amazon\'s recruiting AI had to be scrapped after it began favoring male applicants — mirroring historical hiring patterns.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Credit scoring algorithms have denied loans based on zip codes, unintentionally linking decisions to geographic and demographic patterns.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'These aren\'t small glitches. They remind us that AI isn\'t neutral — it reflects the data we feed it. That\'s why ethical design, diverse datasets, and human oversight are critical. AI is powerful, but it must be responsible.'
          }
        ]
      },
      {
        _type: 'image',
        asset: { url: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=400&fit=crop' },
        alt: 'AI Ethics and Bias'
      },
      {
        _type: 'block',
        style: 'h2',
        children: [
          {
            _type: 'span',
            text: 'AI Around You (Even If You Don\'t Notice)'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Even if you feel like you\'re not "using AI," you probably used it multiple times today:'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Spotify learns what you skip and builds your perfect playlist.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Gmail Smart Reply finishes your sentences ("Got it, thanks!").'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'LinkedIn recommends people you may know — or jobs you may like.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Your bank flags suspicious transactions that don\'t match your usual behavior.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Voice assistants like Siri or Alexa turn your speech into actions.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'AI is already woven into your daily life — helping you save time, make decisions, and find things faster.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'h2',
        children: [
          {
            _type: 'span',
            text: 'Can AI Think Like Us?'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Not really. AI can analyze, generate, and predict — but it doesn\'t truly understand. A simple comparison:'
          }
        ]
      },
      {
        _type: 'block',
        style: 'h3',
        children: [
          {
            _type: 'span',
            text: 'Situation: A friend says "I\'m fine," but you can tell they\'re upset.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Human Intuition: You sense tone, emotion, context.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'AI Logic: AI reads "I\'m fine" and assumes it\'s true.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'h3',
        children: [
          {
            _type: 'span',
            text: 'Situation: You create a new workaround at work.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Human Intuition: You use judgment, experience, creativity.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'AI Logic: AI repeats patterns it has seen before.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Humans connect emotion + experience + context. AI connects data points. It\'s not creativity — it\'s probability. And that\'s okay. It just means AI thinks differently, and it still needs humans for empathy, ethics, and big-picture judgment.'
          }
        ]
      },
      {
        _type: 'image',
        asset: { url: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=400&fit=crop' },
        alt: 'Human vs AI Thinking'
      },
      {
        _type: 'block',
        style: 'h2',
        children: [
          {
            _type: 'span',
            text: 'The Real Power of AI'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'AI isn\'t here to replace people — it\'s here to amplify them. Used well, AI can:'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Automate repetitive tasks so humans can focus on strategy.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Help doctors detect diseases earlier.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Identify cyber threats before they spread.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Translate languages instantly across borders.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Personalize learning for every student.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'Improve customer experiences at scale.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'The magic happens when human creativity meets machine efficiency. AI is a mirror: it reflects both our strengths and our flaws. The more responsibly we build it, the more powerful it becomes.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'h2',
        children: [
          {
            _type: 'span',
            text: 'Final Thought'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'AI isn\'t magic. It\'s not a villain waiting to take over the world. And it\'s definitely not a replacement for human intelligence. It\'s a tool — one powered by data, designed by people, and shaped by our values. So the next time Netflix knows exactly what you feel like watching, or your email suggests the perfect response, smile. That\'s AI quietly doing its job — learning, predicting, adapting.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'h2',
        children: [
          {
            _type: 'span',
            text: 'How We See AI at Cygne'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'At Cygne, we believe AI should strengthen human decision-making — not replace it. Our focus is on building and applying AI responsibly, transparently, and in ways that create real value for people, teams, and businesses. We see AI as a tool that amplifies human potential, and we\'re committed to helping organizations use it thoughtfully and effectively.'
          }
        ]
      },
      {
        _type: 'block',
        style: 'normal',
        children: [
          {
            _type: 'span',
            text: 'AI won\'t replace humans. But humans who understand AI may replace those who don\'t.'
          }
        ]
      }
    ]
  }
};

// Function to get relevant placeholder image based on category or title
const getPlaceholderImage = (post) => {
  const category = post.category?.toLowerCase() || '';
  const title = post.title?.toLowerCase() || '';
  
  if (category.includes('ai') || title.includes('ai') || title.includes('artificial intelligence')) {
    return 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=500&h=300&fit=crop';
  }
  if (category.includes('cloud') || title.includes('cloud')) {
    return 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=500&h=300&fit=crop';
  }
  if (category.includes('architecture') || title.includes('zero trust') || title.includes('architecture')) {
    return 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=500&h=300&fit=crop';
  }
  if (category.includes('automation') || title.includes('automation') || title.includes('incident')) {
    return 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=500&h=300&fit=crop';
  }
  if (category.includes('ransomware') || title.includes('ransomware') || title.includes('malware')) {
    return 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=500&h=300&fit=crop';
  }
  if (category.includes('compliance') || title.includes('compliance') || title.includes('regulation')) {
    return 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&h=300&fit=crop';
  }
  // Default cybersecurity image
  return 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=500&h=300&fit=crop';
};

export default function SimpleBlogDetails() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        // First try to fetch by ID (since we're using ID as slug)
        const queryById = `*[_type == "post" && _id == $slug][0] {
          _id,
          title,
          body,
          excerpt,
          category,
          readTime,
          author,
          tags,
          featured,
          publishedAt,
          mainImage{
            asset->{
              _id,
              url
            },
            alt
          }
        }`;
        
        let data = await client.fetch(queryById, { slug });
        
        // If not found by ID, try by slug
        if (!data) {
          const queryBySlug = `*[_type == "post" && slug.current == $slug][0] {
            _id,
            title,
            body,
            excerpt,
            category,
            readTime,
            author,
            tags,
            featured,
            publishedAt,
            mainImage{
              asset->{
                _id,
                url
              },
              alt
            }
          }`;
          data = await client.fetch(queryBySlug, { slug });
        }
        
        if (data) {
          setPost(data);
        } else {
          setPost(samplePosts[slug]);
        }
      } catch (error) {
        console.error('Error fetching post:', error);
        setPost(samplePosts[slug]);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-blue-600">LOADING ARTICLE...</h1>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white text-gray-900">
        <div className="container mx-auto px-6 py-32 text-center">
          <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-12 font-bold transition-colors duration-300 group text-lg">
            <span className="mr-3 transform group-hover:-translate-x-1 transition-transform duration-300">←</span>
            BACK TO CYBER INSIGHTS
          </Link>
          <div className="max-w-3xl mx-auto">
            <h1 className="text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-700 mb-8">404</h1>
            <h2 className="text-4xl font-black text-gray-900 mb-8">ARTICLE NOT FOUND</h2>
            <p className="text-gray-600 mb-12 text-xl">
              The cybersecurity insight you're looking for doesn't exist or has been moved to a secure location.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>

        <div className="relative container mx-auto px-6 py-12">
          <Link to="/" className="inline-flex items-center bg-white/10 backdrop-blur-md text-white px-6 py-3 rounded-full mb-12 font-semibold transition-all duration-300 group border border-white/20 hover:bg-white/20 hover:scale-105">
            <span className="mr-3 transform group-hover:-translate-x-1 transition-transform duration-300">←</span>
            Back to Articles
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="flex flex-wrap items-center gap-4">
                {post.category && (
                  <span className="bg-gradient-to-r from-cyan-400 to-blue-400 text-slate-900 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    {post.category}
                  </span>
                )}
                <div className="flex items-center gap-6 text-white/80">
                  {post.readTime && (
                    <div className="flex items-center gap-2">
                      <FaClock className="text-cyan-400" />
                      <span className="text-sm font-medium">{post.readTime}</span>
                    </div>
                  )}
                  {post.author && (
                    <div className="flex items-center gap-2">
                      <FaUser className="text-cyan-400" />
                      <span className="text-sm font-medium">{post.author}</span>
                    </div>
                  )}
                  {post.publishedAt && (
                    <div className="flex items-center gap-2">
                      <FaCalendar className="text-cyan-400" />
                      <span className="text-sm font-medium">
                        {new Date(post.publishedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <h1 className="text-5xl lg:text-6xl font-black leading-tight bg-gradient-to-r from-white via-cyan-100 to-blue-100 bg-clip-text text-transparent">
                {post.title}
              </h1>

              {post.excerpt && (
                <p className="text-xl text-white/90 leading-relaxed max-w-2xl">
                  {post.excerpt}
                </p>
              )}

              {/* Social Share Buttons */}
              <div className="flex items-center gap-4">
                <span className="text-white/70 font-medium">Share:</span>
                <div className="flex gap-3">
                  <button 
                    onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`, '_blank')}
                    className="p-3 bg-white/10 hover:bg-blue-600 rounded-full transition-all duration-300 hover:scale-110"
                  >
                    <FaTwitter className="text-white text-lg" />
                  </button>
                  <button 
                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}
                    className="p-3 bg-white/10 hover:bg-blue-700 rounded-full transition-all duration-300 hover:scale-110"
                  >
                    <FaLinkedin className="text-white text-lg" />
                  </button>
                  <button 
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                    className="p-3 bg-white/10 hover:bg-blue-800 rounded-full transition-all duration-300 hover:scale-110"
                  >
                    <FaFacebook className="text-white text-lg" />
                  </button>
                  <button 
                    onClick={() => window.open(`https://www.instagram.com/`, '_blank')}
                    className="p-3 bg-white/10 hover:bg-pink-600 rounded-full transition-all duration-300 hover:scale-110"
                  >
                    <FaInstagram className="text-white text-lg" />
                  </button>
                  <button 
                    onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(post.title + ' ' + window.location.href)}`, '_blank')}
                    className="p-3 bg-white/10 hover:bg-green-600 rounded-full transition-all duration-300 hover:scale-110"
                  >
                    <FaWhatsapp className="text-white text-lg" />
                  </button>
                  <button 
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: post.title,
                          url: window.location.href
                        });
                      } else {
                        navigator.clipboard.writeText(window.location.href);
                        alert('Link copied to clipboard!');
                      }
                    }}
                    className="p-3 bg-white/10 hover:bg-cyan-500 rounded-full transition-all duration-300 hover:scale-110"
                  >
                    <FaShare className="text-white text-lg" />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                {post.mainImage?.asset?.url ? (
                  <img
                    src={post.mainImage.asset.url}
                    alt={post.mainImage.alt || post.title}
                    className="w-full max-w-lg h-80 object-cover rounded-3xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <img
                    src={getPlaceholderImage(post)}
                    alt={post.title}
                    className="w-full max-w-lg h-80 object-cover rounded-3xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                  />
                )}
                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full opacity-20 animate-pulse"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full opacity-30 animate-pulse delay-1000"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="relative mt-8 z-10">
        <article className="container mx-auto px-2">
          {/* Main Content Card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 transform hover:scale-[1.01] transition-all duration-700">
            {/* Progress Bar */}
            <div className="h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 animate-pulse"></div>
            
            {/* Author Section */}
            <div className="px-8 md:px-16 py-8 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-100 hover:from-blue-50 hover:to-purple-50 transition-all duration-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 group">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                    <FaUser className="text-white text-xl group-hover:animate-bounce" />
                  </div>
                  <div className="transform group-hover:translate-x-2 transition-transform duration-300">
                    <h4 className="text-xl font-bold text-gray-900">{post.author || 'Cygne Noir Team'}</h4>
                    <p className="text-gray-600 font-medium">Cybersecurity Expert</p>
                  </div>
                </div>
                <div className="text-right transform hover:scale-105 transition-transform duration-300">
                  <p className="text-sm text-gray-500 uppercase tracking-wide font-semibold">Published</p>
                  <p className="text-lg font-bold text-gray-900">
                    {post.publishedAt ? new Date(post.publishedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }) : 'Recently'}
                  </p>
                </div>
              </div>
            </div>

            {/* Article Body */}
            <div className="px-8 md:px-16 py-12">
              <div className="max-w-6xl mx-auto">
                {post.body ? (
                  <PortableText 
                    value={post.body}
                    components={{
                      types: {
                        image: ({value}) => {
                          const imageUrl = value.asset?.url || value.asset?._ref ? 
                            `https://cdn.sanity.io/images/x9c1zj30/production/${value.asset._ref.replace('image-', '').replace('-jpg', '.jpg').replace('-png', '.png').replace('-webp', '.webp')}` :
                            value.asset;
                          return (
                            <figure className="my-12 -mx-8 md:-mx-16 group">
                              <img 
                                src={imageUrl} 
                                alt={value.alt || 'Blog image'}
                                className="w-full h-96 object-cover shadow-2xl group-hover:shadow-3xl transform group-hover:scale-[1.02] transition-all duration-700"
                              />
                              {value.alt && (
                                <figcaption className="bg-gray-900 text-white px-8 md:px-16 py-4 text-center italic transform group-hover:bg-blue-900 transition-colors duration-500">
                                  {value.alt}
                                </figcaption>
                              )}
                            </figure>
                          )
                        }
                      },
                      block: {
                        h1: ({children}) => (
                          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mt-16 mb-8 leading-tight">
                            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
                              {children}
                            </span>
                          </h1>
                        ),
                        h2: ({children}) => (
                          <div className="mt-16 mb-8">
                            <div className="flex items-center mb-4">
                              <div className="w-2 h-12 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full mr-6"></div>
                              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                                {children}
                              </h2>
                            </div>
                          </div>
                        ),
                        h3: ({children}) => (
                          <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mt-12 mb-6 leading-tight border-b-2 border-blue-100 pb-3">
                            {children}
                          </h3>
                        ),
                        normal: ({children}) => (
                          <p className="text-xl text-gray-700 leading-relaxed mb-8 font-light">
                            {children}
                          </p>
                        )
                      }
                    }}
                  />
                ) : (
                  <div className="space-y-12">
                    {/* Opening Paragraph with Drop Cap */}
                    <div className="relative">
                      <p className="text-xl text-gray-700 leading-relaxed font-light first-letter:text-8xl first-letter:font-black first-letter:text-transparent first-letter:bg-gradient-to-br first-letter:from-blue-600 first-letter:to-purple-600 first-letter:bg-clip-text first-letter:float-left first-letter:mr-4 first-letter:mt-2 first-letter:leading-none">
                        {post.content || post.excerpt}
                      </p>
                    </div>
                    
                    {/* Key Insight Callout */}
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border-2 border-blue-200 shadow-xl group hover:shadow-2xl hover:border-blue-300 transition-all duration-500">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 group-hover:h-2 transition-all duration-300"></div>
                      <div className="p-8 group-hover:bg-gradient-to-br group-hover:from-blue-60 group-hover:via-indigo-60 group-hover:to-purple-60 transition-all duration-500">
                        <div className="flex items-start space-x-6">
                          <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                            <span className="text-white text-3xl group-hover:animate-pulse">💡</span>
                          </div>
                          <div className="transform group-hover:translate-x-2 transition-transform duration-300">
                            <h3 className="text-2xl font-bold text-blue-900 mb-4 group-hover:text-blue-800 transition-colors duration-300">Key Security Insight</h3>
                            <p className="text-lg text-blue-800 leading-relaxed font-medium group-hover:text-blue-700 transition-colors duration-300">
                              This cybersecurity insight provides essential knowledge for protecting your organization against modern threats and emerging vulnerabilities in today's rapidly evolving digital landscape.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Best Practices & Risks Grid */}
                    <div className="grid lg:grid-cols-2 gap-8">
                      {/* Best Practices */}
                      <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-50 to-green-100 border-2 border-emerald-200 shadow-xl hover:shadow-2xl hover:border-emerald-300 transition-all duration-500 hover:-translate-y-2">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-green-500 group-hover:h-2 transition-all duration-300"></div>
                        <div className="p-8 group-hover:bg-gradient-to-br group-hover:from-emerald-60 group-hover:to-green-110 transition-all duration-500">
                          <div className="flex items-center space-x-4 mb-6">
                            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                              <span className="text-white text-2xl group-hover:animate-bounce">✅</span>
                            </div>
                            <h4 className="text-2xl font-bold text-emerald-900 group-hover:text-emerald-800 transition-colors duration-300">Security Best Practices</h4>
                          </div>
                          <ul className="space-y-4">
                            {[
                              'Regular comprehensive security assessments',
                              'Continuous employee training programs', 
                              'Multi-factor authentication deployment',
                              'Real-time monitoring and threat detection'
                            ].map((item, index) => (
                              <li key={index} className="flex items-start space-x-4 group-hover:translate-x-2 transition-transform duration-300" style={{transitionDelay: `${index * 100}ms`}}>
                                <div className="flex-shrink-0 w-3 h-3 bg-emerald-500 rounded-full mt-2 shadow-sm group-hover:shadow-md group-hover:scale-125 transition-all duration-300"></div>
                                <span className="text-lg text-emerald-800 font-medium leading-relaxed group-hover:text-emerald-700 transition-colors duration-300">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      {/* Common Risks */}
                      <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-rose-50 to-red-100 border-2 border-rose-200 shadow-xl hover:shadow-2xl hover:border-rose-300 transition-all duration-500 hover:-translate-y-2">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-rose-500 to-red-500 group-hover:h-2 transition-all duration-300"></div>
                        <div className="p-8 group-hover:bg-gradient-to-br group-hover:from-rose-60 group-hover:to-red-110 transition-all duration-500">
                          <div className="flex items-center space-x-4 mb-6">
                            <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                              <span className="text-white text-2xl group-hover:animate-pulse">⚠️</span>
                            </div>
                            <h4 className="text-2xl font-bold text-rose-900 group-hover:text-rose-800 transition-colors duration-300">Common Security Risks</h4>
                          </div>
                          <ul className="space-y-4">
                            {[
                              'Outdated security policies and procedures',
                              'Insufficient access controls and permissions',
                              'Inadequate incident response planning',
                              'Weak password management systems'
                            ].map((item, index) => (
                              <li key={index} className="flex items-start space-x-4 group-hover:translate-x-2 transition-transform duration-300" style={{transitionDelay: `${index * 100}ms`}}>
                                <div className="flex-shrink-0 w-3 h-3 bg-rose-500 rounded-full mt-2 shadow-sm group-hover:shadow-md group-hover:scale-125 transition-all duration-300"></div>
                                <span className="text-lg text-rose-800 font-medium leading-relaxed group-hover:text-rose-700 transition-colors duration-300">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Tags Section */}
            {post.tags && post.tags.filter(tag => typeof tag === 'string').length > 0 && (
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-8 md:px-16 py-10 border-t border-gray-100">
                <div className="max-w-6xl mx-auto">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                      <span className="text-white text-xl">🏷️</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Related Topics</h3>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {post.tags.filter(tag => typeof tag === 'string').map((tag, index) => (
                      <span key={index} className="bg-white text-blue-700 px-6 py-3 rounded-full text-lg font-semibold border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-50 hover:scale-105 hover:-translate-y-1 transition-all duration-300 cursor-pointer shadow-md hover:shadow-lg transform" style={{animationDelay: `${index * 100}ms`}}>
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Call to Action */}
          <div className="mt-16 mb-24">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 shadow-2xl">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  backgroundSize: '60px 60px'
                }}></div>
              </div>
              
              <div className="relative z-10 px-12 py-16 text-center">
                <div className="max-w-3xl mx-auto">
                  <h3 className="text-4xl md:text-5xl font-black mb-6">
                    <span className="bg-gradient-to-r from-white via-cyan-100 to-blue-100 bg-clip-text text-transparent">
                      Ready for More Insights?
                    </span>
                  </h3>
                  <p className="text-xl text-white/90 mb-10 leading-relaxed font-light">
                    Explore our comprehensive library of cybersecurity articles, threat analysis, and expert guidance to strengthen your organization's security posture.
                  </p>
                  <Link 
                    to="/"
                    className="inline-flex items-center bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white px-12 py-5 rounded-2xl font-bold text-xl transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-cyan-500/25"
                  >
                    <span className="mr-4 text-2xl">📚</span>
                    Explore All Articles
                    <span className="ml-4 transform group-hover:translate-x-2 transition-transform duration-300">→</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}